/**
 * Bridge-on-delete: keep milestones in the surviving chain.
 *
 * Milestones are host-anchored markers, not dependency nodes. Deleting their
 * host without re-anchoring orphans them and they drop out of the chain.
 * When the user bridges, each milestone on a deleted host inherits a surviving
 * neighbor so it stays between the same upstream and downstream work.
 *
 * Placement:
 * - Unique surviving upstream → attach as that host's output
 *   (M1 → M2 → MS → M3, delete M2 → MS becomes output of M1)
 * - Else unique surviving downstream → attach as that host's input
 *   (fan-in into M2 → MS → M3 → MS becomes input of M3)
 */

import { getEffectiveDependencyIds } from './nestDependencyInfo';

const isWorkNode = (task) =>
  Boolean(task) && task.nodeType !== 'milestone' && task.nodeType !== 'note';

function buildForwardAdjacency(tasks) {
  const adj = new Map();
  for (const task of tasks) {
    for (const depId of getEffectiveDependencyIds(task, tasks)) {
      if (!depId) continue;
      if (!adj.has(depId)) adj.set(depId, []);
      adj.get(depId).push(task.id);
    }
  }
  return adj;
}

/** Deleted nodes reachable forward from host, including the host. */
function deletedForwardClosure(hostId, tasks, deletedSet) {
  const adj = buildForwardAdjacency(tasks);
  const reachable = new Set([hostId]);
  const queue = [hostId];
  while (queue.length) {
    const id = queue.shift();
    for (const next of adj.get(id) || []) {
      if (!deletedSet.has(next) || reachable.has(next)) continue;
      reachable.add(next);
      queue.push(next);
    }
  }
  return reachable;
}

function survivingUpstreamIds(hostId, tasks, deletedSet) {
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const seen = new Set();
  const result = new Set();

  const walk = (id) => {
    if (seen.has(id)) return;
    seen.add(id);
    const task = byId.get(id);
    if (!task) return;
    for (const depId of getEffectiveDependencyIds(task, tasks)) {
      if (!depId) continue;
      if (deletedSet.has(depId)) {
        walk(depId);
        continue;
      }
      if (isWorkNode(byId.get(depId))) result.add(depId);
    }
  };

  walk(hostId);
  return [...result];
}

function survivingDownstreamIds(hostId, tasks, deletedSet) {
  const closure = deletedForwardClosure(hostId, tasks, deletedSet);
  const result = [];
  for (const task of tasks) {
    if (deletedSet.has(task.id) || !isWorkNode(task)) continue;
    const deps = getEffectiveDependencyIds(task, tasks);
    if (deps.some((depId) => closure.has(depId))) result.push(task.id);
  }
  return result;
}

function chooseInheritedAnchor(upstreams, downstreams) {
  if (upstreams.length === 1) {
    return { hostTaskId: upstreams[0], side: 'output' };
  }
  if (downstreams.length === 1) {
    return { hostTaskId: downstreams[0], side: 'input' };
  }
  return null;
}

/**
 * Compute new milestoneAnchor values for milestones whose host is being deleted.
 *
 * @param {Array} tasks - all tasks (pre-deletion)
 * @param {Array<string>|Set<string>} deletedIds - ids being deleted
 * @returns {Object} map of milestoneId → { hostTaskId, side }
 */
export function computeBridgedMilestoneAnchors(tasks, deletedIds) {
  const deletedSet = deletedIds instanceof Set ? deletedIds : new Set(deletedIds);
  const byId = new Map((tasks || []).map((t) => [t.id, t]));
  const hostCache = new Map();
  const patch = {};

  for (const task of tasks || []) {
    if (task.nodeType !== 'milestone') continue;
    if (deletedSet.has(task.id)) continue;

    const hostId = task.milestoneAnchor?.hostTaskId;
    if (!hostId || !deletedSet.has(hostId)) continue;

    let inherited = hostCache.get(hostId);
    if (inherited === undefined) {
      const upstreams = survivingUpstreamIds(hostId, tasks, deletedSet);
      const downstreams = survivingDownstreamIds(hostId, tasks, deletedSet);
      inherited = chooseInheritedAnchor(upstreams, downstreams);
      hostCache.set(hostId, inherited);
    }

    if (!inherited || inherited.hostTaskId === task.id) continue;
    if (deletedSet.has(inherited.hostTaskId)) continue;
    if (!isWorkNode(byId.get(inherited.hostTaskId))) continue;

    patch[task.id] = { ...inherited };
  }

  return patch;
}
